import React, { useEffect, useMemo, useState } from 'react';
import { X, Newspaper, TrendingUp, Network, Flag, Settings, Bell, Tag as TagIcon, Menu, ChevronRight, CheckCheck, UserRound } from 'lucide-react';
import { Card, Modal, Segmented, Tag, Empty } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { currentUser } from '../../pages/Social/socialStore';
import { UserAvatar } from '../../pages/Social/component/UserAvatar';
import { COMPANY_NAME, NOTICE_CATEGORY_COLOR, SYSTEM_NOTICES, useNoticeReads, type SystemNotice } from './userHub';

const menuItems = [
  { key: 'feed', icon: Newspaper, label: 'Bảng tin', path: '/social' },
  { key: 'career', icon: TrendingUp, label: 'Lộ trình thăng tiến', path: '/career-progression' },
  { key: 'orgchart', icon: Network, label: 'Sơ đồ tổ chức', path: '/so-do-to-chuc' },
  { key: 'workprocess', icon: Flag, label: 'Quá trình làm việc', path: '/work-process-page' },
];

/**
 * Khung chung của các trang có sidebar này (Bảng tin, Lộ trình thăng tiến, Sơ đồ tổ chức, Quá trình làm việc):
 * full chiều rộng vùng nội dung để sidebar luôn đứng cùng một chỗ khi chuyển trang.
 */
export const USER_PAGE_ROW = 'w-full flex flex-col lg:flex-row gap-4 md:gap-5';

/** Số thông báo hiện sẵn ở sidebar; còn lại xem trong "Xem tất cả" */
const SIDEBAR_NOTICES = 3;

const fmtTime = (iso: string) => dayjs(iso).format('HH:mm DD/MM/YYYY');

function NoticeRow({ n, unread, onOpen, compact }: { n: SystemNotice; unread: boolean; onOpen: (n: SystemNotice) => void; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      className={`w-full text-left flex items-start gap-3 ${compact ? 'px-4 py-3' : 'px-3 py-3 rounded-lg'} hover:bg-gray-50 dark:hover:bg-gray-700/40 transition cursor-pointer`}
    >
      <span className="relative flex-shrink-0 mt-0.5">
        <Bell className={`w-[18px] h-[18px] ${unread ? 'text-[#3e91ab]' : 'text-gray-400 dark:text-gray-500'}`} />
        {unread && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-800" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm leading-snug line-clamp-2 ${unread ? 'font-bold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-600 dark:text-gray-300'}`}>{n.title}</span>
        <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mt-1.5">
          <span>{fmtTime(n.time)}</span>
          <span>•</span>
          <TagIcon className="w-3 h-3" />
          <span>{n.category}</span>
        </span>
      </span>
    </button>
  );
}

function NoticeDetail({ n, onBack }: { n: SystemNotice; onBack?: () => void }) {
  return (
    <div>
      {onBack && <button type="button" onClick={onBack} className="text-sm font-semibold text-[#3e91ab] hover:underline mb-3 cursor-pointer">← Tất cả thông báo</button>}
      <div className="flex items-center gap-2 mb-2">
        <Tag color={NOTICE_CATEGORY_COLOR[n.category]}>{n.category}</Tag>
        <span className="text-xs text-gray-400">{fmtTime(n.time)}</span>
      </div>
      <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-3">{n.title}</h3>
      <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {n.content.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}

export default function UserSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const me = useMemo(currentUser, []);
  const { read, markRead, markAllRead, unread } = useNoticeReads(me.id);
  const [mobileOpen, setMobileOpen] = useState(false);
  // null = đóng; 'list' = danh sách tất cả; còn lại = đang xem 1 thông báo (from: mở từ danh sách hay từ sidebar)
  const [dialog, setDialog] = useState<{ view: 'list' } | { view: 'detail'; notice: SystemNotice; fromList: boolean } | null>(null);
  const [listFilter, setListFilter] = useState<'all' | 'unread'>('all');

  const activeKey = menuItems.find(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))?.key ?? 'feed';

  // đóng menu mobile khi chuyển trang
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const notices = useMemo(() => [...SYSTEM_NOTICES].sort((a, b) => b.time.localeCompare(a.time)), []);
  const openNotice = (n: SystemNotice, fromList = false) => {
    markRead(n.id);
    setDialog({ view: 'detail', notice: n, fromList });
  };
  const listed = listFilter === 'unread' ? notices.filter(n => !read.includes(n.id)) : notices;

  const sidebarContent = (
    <div className="flex flex-col space-y-4 w-full">
      {/* Thẻ hồ sơ: bấm để mở trang thông tin cá nhân */}
      <Card className="!rounded-xl !border-gray-200 dark:!border-gray-700 dark:!bg-gray-800 overflow-hidden shadow-sm" styles={{ body: { padding: 0 } }}>
        <button type="button" onClick={() => navigate('/admin/profile')} title="Xem / sửa thông tin cá nhân" className="w-full text-left group cursor-pointer">
          <div className="h-20 bg-gradient-to-br from-blue-200 via-indigo-100 to-purple-200 dark:from-blue-900/60 dark:via-gray-800 dark:to-purple-900/60 relative">
            <div className="absolute -bottom-7 left-5">
              <UserAvatar name={me.name} src={me.avatar} size={60} className="border-4 border-white dark:border-gray-800 shadow-sm" />
            </div>
            <span className="absolute top-2 right-2 flex items-center gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 rounded-full px-2 py-0.5 opacity-0 group-hover:opacity-100 transition">
              <UserRound className="w-3 h-3" /> Hồ sơ của tôi
            </span>
          </div>
          <div className="pt-9 pb-4 px-5">
            <div className="font-bold text-gray-900 dark:text-gray-100 text-base leading-tight group-hover:text-[#3e91ab] transition">{me.name}</div>
            {me.subtitle && <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{me.subtitle}</div>}
            <div className="text-sm text-gray-500 dark:text-gray-400">{COMPANY_NAME}</div>
          </div>
        </button>
      </Card>

      {/* Menu */}
      <Card className="!rounded-xl !border-gray-200 dark:!border-gray-700 dark:!bg-gray-800 shadow-sm" styles={{ body: { padding: 8 } }}>
        <nav className="space-y-1" aria-label="Menu nhân sự">
          {menuItems.map(({ key, icon: Icon, label, path }) => {
            const isActive = activeKey === key;
            return (
              <button
                key={key}
                onClick={() => { setMobileOpen(false); if (!isActive) navigate(path); }}
                aria-current={isActive ? 'page' : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-left transition cursor-pointer
                  ${isActive ? 'bg-cyan-50 text-[#3e91ab] dark:bg-cyan-950/40 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-[#3e91ab] dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </nav>
      </Card>

      {/* Thông báo hệ thống */}
      <Card className="!rounded-xl !border-gray-200 dark:!border-gray-700 dark:!bg-gray-800 relative overflow-hidden shadow-sm" styles={{ body: { padding: 0 } }}>
        <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Settings className="w-[18px] h-[18px] text-[#3e91ab] flex-shrink-0" />
            <span className="font-bold text-[15px] text-[#3e91ab] truncate">Thông báo hệ thống</span>
            {unread > 0 && <span className="text-[11px] font-bold bg-red-500 text-white rounded-full px-1.5 leading-[18px] min-w-[18px] text-center">{unread}</span>}
          </div>
          {unread > 0 && (
            <button type="button" onClick={markAllRead} title="Đánh dấu tất cả là đã đọc" className="text-gray-400 hover:text-[#3e91ab] cursor-pointer flex-shrink-0">
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {notices.slice(0, SIDEBAR_NOTICES).map(n => <NoticeRow key={n.id} n={n} unread={!read.includes(n.id)} onOpen={openNotice} compact />)}
        </div>
        {notices.length > SIDEBAR_NOTICES && (
          <button type="button" onClick={() => { setListFilter('all'); setDialog({ view: 'list' }); }}
            className="w-full px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 text-xs font-bold text-[#3e91ab] hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center justify-center gap-1 cursor-pointer">
            Xem tất cả ({notices.length}) <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </Card>
    </div>
  );

  return (
    <>
      {/* Nút mở menu trên tablet / mobile */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setMobileOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm text-gray-700 dark:text-gray-200 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer"
        >
          <Menu className="w-4 h-4 text-[#3e91ab]" />
          <span>{menuItems.find(m => m.key === activeKey)?.label || 'Menu quản lý'}</span>
          {unread > 0 && <span className="w-2 h-2 rounded-full bg-red-500" aria-label={`${unread} thông báo chưa đọc`} />}
        </button>
      </div>

      {mobileOpen && <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-black/50 z-50 lg:hidden" />}

      {/* Drawer trên mobile / tablet */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-gray-50 dark:bg-gray-900 p-4 overflow-y-auto shadow-2xl transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between mb-3 px-1">
          <span className="font-bold text-gray-800 dark:text-gray-100">Menu quản lý</span>
          <button onClick={() => setMobileOpen(false)} aria-label="Đóng menu" className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-200/60 dark:hover:bg-gray-800 transition cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>
        {sidebarContent}
      </div>

      {/* Sidebar cố định trên desktop */}
      <div className="hidden lg:flex lg:w-72 flex-shrink-0 flex-col space-y-4 self-start lg:sticky lg:top-4 lg:overflow-y-auto">
        {sidebarContent}
      </div>

      <Modal
        open={!!dialog}
        onCancel={() => setDialog(null)}
        footer={null}
        width={560}
        destroyOnHidden
        title={dialog?.view === 'list' ? (
          <div className="flex items-center justify-between gap-3 pr-8">
            <span>Thông báo hệ thống</span>
            {unread > 0 && <button type="button" onClick={markAllRead} className="text-xs font-semibold text-[#3e91ab] hover:underline cursor-pointer">Đánh dấu tất cả đã đọc</button>}
          </div>
        ) : 'Thông báo hệ thống'}
      >
        {dialog?.view === 'list' && (
          <>
            <Segmented
              size="small"
              value={listFilter}
              onChange={v => setListFilter(v as 'all' | 'unread')}
              options={[{ label: `Tất cả (${notices.length})`, value: 'all' }, { label: `Chưa đọc (${unread})`, value: 'unread' }]}
              className="mb-2"
            />
            {listed.length
              ? <div className="max-h-[60vh] overflow-y-auto -mx-3">{listed.map(n => <NoticeRow key={n.id} n={n} unread={!read.includes(n.id)} onOpen={x => openNotice(x, true)} />)}</div>
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bạn đã đọc hết thông báo" />}
          </>
        )}
        {dialog?.view === 'detail' && <NoticeDetail n={dialog.notice} onBack={dialog.fromList ? () => setDialog({ view: 'list' }) : undefined} />}
      </Modal>
    </>
  );
}
